import { TooltipContextProvider } from '@stormwater-software/lib-ui';
import _, { findIndex } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { ProjectPermissionState } from '../../contexts/ProjectPermissionContext';
import RolesContext from '../../contexts/RolesContext';
import withProjectPermissionContext from '../../hoc/withProjectPermissionContext';
import uploadApi from '../../utils/api/upload';
import ConfirmationModal from '../Shared/ConfirmationModal/ConfirmationModal';
import ConvertDocModal from '../Shared/ConvertDocument/ConvertDocModal';
import FileRenameModal from '../Shared/FileRow/FileRenameModal';
import FileCardList from '../Shared/FileRow/List';
import WebViewerModal from '../Shared/PDFEditor/WebViewerModal';
import SuccessModal from '../Shared/SuccessModal/SuccessModal';
import clientApi from '../../utils/api/client';
import { ProjectContext } from '../../contexts/ProjectContext';
import { getProjectConfigHelpers } from './Forms/projectFormSchema';
import { toast } from "react-toastify";
import AppContext from '../../contexts/AppContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import projectApi from '../../utils/api/project';
import TrackPermitModal from '../Shared/FileRow/TrackPermitModal';
import permitApi from '../../utils/api/permit';

const allowedTypes = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'application/pdf',
];

export interface IProjectDocumentsProps {
  documentTypes: Array<any>;
  regulations: Array<any>;
  onDocumentDelete: (guid: string) => void;
  onDocumentUpload: (search?: any) => void;
  projectId: number;
  onMove: (e: MouseEvent, name: string) => void;
  onDrop: (e: MouseEvent, name: string) => void;
  shouldRenderRegulations: () => void;
  projectPermissionContext: ProjectPermissionState;
}

const ProjectDocuments: React.FC<IProjectDocumentsProps> = ({
  documentTypes,
  onDocumentDelete,
  onDocumentUpload,
  projectId,
  onMove,
  onDrop,
  shouldRenderRegulations,
  projectPermissionContext,
}) => {
  const rolesContext = useContext(RolesContext);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [showDownload, setShowDownload] = useState<boolean>(false);
  const [rename, setRename] = useState<boolean>(false);
  const [showTrackPermitModal, setShowTrackPermitModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [filename, setFilename] = useState<string>('');
  const [docGuid, setDocGuid] = useState<string>('');
  const [docId, setDocId] = useState<number>(-1);
  const [errorText, setErrorText] = useState<string>('');
  const [showConvert, setShowConvert] = useState<boolean>(false);
  const [convertQueue, setConvertQueue] = useState<Array<any>>([]);
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [docType, setDocType] = useState<any>({});

  /** Surface an API failure through the page's error modal. */
  const showApiError = (err: any) => {
    setErrorText(
      err?.response?.data?.message
        ? err.response.data.message
        : 'An error has occurred',
    );
    setShowErrorModal(true);
  };
  const projectContext = useContext(ProjectContext)
  const { hasDocEditing } = getProjectConfigHelpers(
    (projectContext.project?.project_config || 'full') as any,
  )

  // Whether documents opened from this page are editable. Regulations included:
  // a project's own annotations live on its own layer, and inherited master
  // ones are locked by the viewer itself (SW2-2067). The regulation row menus
  // used to hardcode this to false, so they read "View" while the viewer still
  // opened fully editable. This one flag drives both the row labels and the
  // viewers, so they cannot drift apart.
  const canEditDocuments =
    hasDocEditing && rolesContext.userHasPermission("update", "Project Documents")
  const [docTypes, setDocTypes] = useState<Array<any>>([])
  const [isAdmin, setIsAdmin] = useState(false)
  const appContext = useContext(AppContext)
  const [shouldReloadDocumentsPage, setShouldReloadDocumentsPage] = useState(false)
  const [selectedSubDocType, setSelectedSubDocType] = useState<any>()
  const searchRef = useRef<HTMLInputElement>(null);

  const [projectRegUploading, setProjectRegUploading] = useState(false);
  const [selectedRegSubFolder, setSelectedRegSubFolder] = useState<any>(null);

  useEffect(() => {
    projectContext.loadDocs(projectId, searchRef.current?.value || "")
  }, [shouldReloadDocumentsPage])

  useEffect(() => {
    setIsAdmin(appContext.get("user").roleName === 'Admin')
  }, [])
  const clientId = projectContext.project && projectContext.project.client_id

  const docTypeRearrange = (data: any) => {
    const newDoc: any = []
    const Doc = data
      .sort((a: any, b: any) => a.order - b.order)
      .map((ele: any) => ele.name)

    Doc.map((newdoc: any) => {

      for (const keys in data) {
        if (data[keys].name === "Active Site Maps" && newdoc === "Active Site Maps") {
          // dont push "Active Site Maps" to newDoc
        } else if (data[keys].name === newdoc) {
          newDoc.push(data[keys])
        }
      }
    })

    for (const keys in data) {
      if (!Doc.includes(data[keys].name)) {
        newDoc.push(data[keys])
      }
    }

    if (!searchRef.current || searchRef.current?.value === '') setDocTypes(newDoc);
    else {
      const filteredDocTypes = newDoc.filter((docType: any) => {
        const subFolderList = docType.documents
          .filter((doc: any) => doc.sub_doc_type)
          .map((doc: any) => doc.sub_doc_type.id);

        // Top-level regulation documents carry a `scope` (master | project)
        // instead of a ProjectUploadModel; the server has already filtered
        // them by the search, so a match there must keep Regulations listed.
        const docsNotInSubfolder = docType.documents.filter((doc: any) => {
          if (doc.scope) return true;
          if (!doc.ProjectUploadModel) return false;

          return (
            !doc.ProjectUploadModel.project_sub_doc_type_id ||
            subFolderList.includes(
              doc.ProjectUploadModel.project_sub_doc_type_id,
            )
          );
        });
        return docType.documents.length > 0 && (docsNotInSubfolder.length > 0 || subFolderList.length > 0);
      });
      setDocTypes(filteredDocTypes);
    }  
  }

  useEffect(() => {

    if (!clientId || !projectContext.project?.doc_type_group_id) return;

    // clientApi.getClientsDocTypes.forProjects(clientId)
    clientApi.documentGroups.getDocTypeGroupItems(clientId,projectContext.project.doc_type_group_id)
      .then((data) => {

         const clientDocTypes= data.map((folder: any) => {
            return {
              doctype_id: folder.doc_type_id,
              name: folder.doc_type?.name || folder.name,
            };
          });
        const combinedArray: any = []
        

        documentTypes.filter((docType:any)=>docType.name!=='Fixed Details').forEach(ele => {
          clientDocTypes.forEach((e: any) => {
            if (e.doctype_id === ele.id) {
              combinedArray.push({ ...ele, order: e.order })
            }
          })
        })

        return combinedArray
      })
      .then(data => docTypeRearrange(data))
    if (clientId)
      clientApi.index([clientId]).then((c: any) => {
        projectContext.setDocumentDate(c[0].document_date);
      });
  }, [projectContext])

  const hideEditor = () => {
    setShowEditor(false);
    setShowDownload(false)
    setDocGuid('');
    setDocId(-1);
  };

  const showRename = async (document: any, documentType: any) => {
    setRename(true);
    setDocGuid(document.GUID);
    setDocId(document.id);
    setFilename(document.name);
    setDocType(documentType);
  };

  const hideRename = () => {
    setDocGuid('');
    setDocId(-1);
    setFilename('');
    setRename(false);
    setDocType({});
  };

  const handleRename = async (newName: any) => {
    await uploadApi.rename(docGuid, newName);
    selectedSubDocType && await reloadSelectedSubDocTypeData()
    hideRename();

    return onDocumentUpload(searchRef.current?.value || "")
  };

  const hideConfirm = () => {
    setConfirm(false);

    return false;
  };

  const onConfirmOverwrite = () => {
    hideConfirm();

    return true;
  };

  const handleUpload = (
    newFile: any,
    projectDocId: number,
  ) => {

    if (selectedSubDocType) {
      return uploadApi.project
        .create(newFile.id, projectId, projectDocId, selectedSubDocType.id)
        .then(() => {
          return onDocumentUpload(searchRef.current?.value || "")
        })
        .catch((err) => {
          setErrorText(
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'An error has occurred',
          );
          setShowErrorModal(true);
        });
    }

    return uploadApi.project
      .create(newFile.id, projectId, projectDocId)
      .then(() => {
        return onDocumentUpload(searchRef.current?.value || "")
      })
      .catch((err) => {
        setErrorText(
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'An error has occurred',
        );
        setShowErrorModal(true);
      });
  }

  const handleDelete = async (doc: any) => {
    permitApi.deleteByFileId(doc.ProjectUploadModel.id)
    uploadApi.project
      .destroy(projectId, doc.id)
      .then(async () => {
        selectedSubDocType && await reloadSelectedSubDocTypeData()
        onDocumentDelete(doc.GUID);
        projectContext.loadDocs(projectId, searchRef.current?.value || "")
      })
      .catch((err) => {
        setErrorText(
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'An error has occurred',
        );
        setShowErrorModal(true);
      });
  }

  const handleDownload = (doc: any) => {
    toast("Download has started")
    setShowDownload(true)
    setDocGuid(doc.GUID);
    setDocId(doc.id);
    setFilename(doc.name);
  }

  const handleReplace = async (
    newFile: any,
    projectDocId: number,
  ) => {
    return uploadApi.project
      .create(newFile.id, projectId, projectDocId)
      .then(() => {
        setIsWaiting(false)

        return onDocumentUpload(searchRef.current?.value || "")
      })
      .catch((err) => {
        setErrorText(
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'An error has occurred',
        );
        setShowErrorModal(true);
      });
  };

  // handle a converted file
  const handleConverted = () => {
    return onDocumentUpload(searchRef.current?.value || "")
  };

  const handleEdit = (document: any) => {
    setShowEditor(true);
    setDocGuid(document.GUID);
    setDocId(document.id);
    setFilename(document.name);
  };

  const onEdit = () => {
    onDocumentUpload(searchRef.current?.value || "")
  };

  const dequeue = (upload: any) => {
    // remove from conversion queue
    const inx = findIndex(convertQueue, {
      id: upload.id,
    });

    if (inx !== null) {
      setConvertQueue([
        ...convertQueue.slice(0, inx),
        ...convertQueue.slice(inx + 1),
      ]);
    }
  };

  const reloadSelectedSubDocTypeData = async () => {
    /**
     * Nothing is selected at a folder's top level, and several callers invoke
     * this unguarded (`Shared/FileRow/List.js`) — so renaming a document from the
     * folder view dereferenced `undefined` here and threw
     * "Cannot read properties of undefined (reading 'project_doc_type_id')".
     * The rename itself succeeded, so the only symptom was an unhandled rejection.
     * Guarding at the definition rather than at each call site (SW2-2067 area).
     */
    if (!selectedSubDocType) {
      return;
    }
    const updatedData = await projectApi.getDocs(projectId, null, searchRef.current?.value || "")
    const relevantDocType = (updatedData.documentTypes ?? []).filter((docTypeEle: any) => docTypeEle.project_doc_type_id === selectedSubDocType.project_doc_type_id)
    // The folder can be gone by the time this runs (deleted, or filtered out by
    // the search box the call reads); there is then nothing to reload.
    if (!relevantDocType.length) {
      return;
    }
    const relevantSubDocType = (relevantDocType[0].documents ?? []).filter((documentEle: any) => documentEle.id === selectedSubDocType.id)

    setSelectedSubDocType(relevantSubDocType[0])
  }

  const reloadSelectedRegSubFolder = async () => {
    const updatedData = await projectApi.getDocs(projectId, null, searchRef.current?.value || "");
    const regDocType = updatedData.documentTypes.find((dt: any) => dt.name === 'Regulations');
    if (regDocType && selectedRegSubFolder) {
      const updated = (regDocType.documents || []).find(
        (d: any) => d.is_regulation_folder && d.id === selectedRegSubFolder.id,
      );
      if (updated) setSelectedRegSubFolder(updated);
    }
  };

  const handleProjectRegUpload = async (newFile: any, documentGroupId: number, regulationSubFolderId?: number | null) => {
    setProjectRegUploading(true);
    try {
      await uploadApi.projectRegulation.create(projectId, newFile.id, documentGroupId, regulationSubFolderId || null);
      projectContext.loadDocs(projectId, searchRef.current?.value || "");
    } catch (err) {
      const e = err as any;
      setErrorText(e?.response?.data?.message || 'Failed to upload project regulation document');
      setShowErrorModal(true);
    } finally {
      setProjectRegUploading(false);
    }
  };

  const handleProjectRegDelete = async (uploadId: number, projectId: number) => {
    try {
      await uploadApi.projectRegulation.destroy(projectId, uploadId);
      projectContext.loadDocs(projectId, searchRef.current?.value || "");
    } catch (err) {
      const e = err as any;
      setErrorText(e?.response?.data?.message || 'Failed to delete project regulation document');
      setShowErrorModal(true);
    }
  };

  const handleSearchFunc = _.debounce(() => {
    projectContext.loadDocs(projectId, searchRef.current?.value || "")
  }, 500);

  return (
    <TooltipContextProvider>
      {!selectedSubDocType && !selectedRegSubFolder && <input
        type='search'
        className='search-control'
        style={{
          width: '100%',
        }}
        ref={searchRef}
        placeholder='Search Documents...'
        defaultValue={searchRef.current?.value || ""}
        onChange={handleSearchFunc}
      />}
      <section className='documents'>
        {selectedRegSubFolder && (
          // ── REGULATION SUBFOLDER VIEW ────────────────────────────────
          <>
            <div
              className='all-files-button'
              onClick={() => setSelectedRegSubFolder(null)}
            >
              <span className='all-files-button-icon'><FontAwesomeIcon icon={faArrowLeft} /></span>
              <span className='all-files-button-text'>All Files</span>
            </div>
            <input type='search' style={{ display: 'none' }} ref={searchRef} defaultValue={searchRef.current?.value || ""} />
            <FileCardList
              key={selectedRegSubFolder.id}
              groupName={selectedRegSubFolder.sub_doc_type?.name}
              actualGroupName="Regulations"
              documents={(selectedRegSubFolder.project_uploads || []).map((ele: any) => ({
                ...ele,
                ProjectRegulationUploadModel: ele.ProjectRegulationUploadModel || ele,
              }))}
              isWaiting={projectRegUploading}
              canDownload={rolesContext.userHasPermission('all', 'Download')}
              canEdit={canEditDocuments}
              canRename={false}
              canReplace={false}
              canDelete={rolesContext.userHasPermission('delete', 'Project Documents')}
              canUpload={rolesContext.userHasPermission('update', 'Project Documents')}
              handleEdit={(doc: any) => handleEdit(doc)}
              handleRename={() => {}}
              handleReplace={() => {}}
              handleDownload={(doc: any) => handleDownload(doc)}
              onUpload={async (newFile: any) => {
                await handleProjectRegUpload(newFile, selectedRegSubFolder.document_group_id, selectedRegSubFolder.id);
                await reloadSelectedRegSubFolder();
              }}
              handleDelete={async (doc: any) => {
                const uploadId = doc.ProjectRegulationUploadModel?.upload_id || doc.id;
                await handleProjectRegDelete(uploadId, projectId);
                await reloadSelectedRegSubFolder();
              }}
              permCheck={rolesContext.userHasPermission}
              label="Project Documents"
              allowedTypes={allowedTypes}
              document_date={projectContext.document_date}
              isAdmin={isAdmin}
              isProjectDocumentsPage={true}
              docTypes={docTypes}
              setShouldReloadDocumentsPage={setShouldReloadDocumentsPage}
              documentType={{ is_regulation_folder: true, document_group_id: selectedRegSubFolder.document_group_id }}
              projectId={projectId}
              selectedSubDocType={selectedRegSubFolder}
              setSelectedSubDocType={() => setSelectedRegSubFolder(null)}
              reloadSelectedSubDocTypeData={reloadSelectedRegSubFolder}
              showAddSubFolderIcon={false}
              canManageSubfolder={selectedRegSubFolder.is_project_folder && rolesContext.userHasPermission('all', 'Manage Subfolders')}
              onMove={() => {}}
              onDrop={() => {}}
              canDrag={rolesContext.userHasPermission('update', 'Project Documents')}
              reloadDocs={() => {
                setSelectedRegSubFolder(null);
                projectContext.loadDocs(projectId, searchRef.current?.value || "");
              }}
            />
          </>
        )}
        {!selectedRegSubFolder && selectedSubDocType && (
          // ── PROJECT DOCUMENT SUBFOLDER VIEW ─────────────────────────
          <>
            <div
              className='all-files-button'
              onClick={() => setSelectedSubDocType("")}
            >
              <span className='all-files-button-icon'><FontAwesomeIcon icon={faArrowLeft} /></span>
              <span className='all-files-button-text'>All Files</span>
            </div>
            <input
              type='search'
              style={{
                display: 'none'
              }}
              ref={searchRef}
              defaultValue={searchRef.current?.value || ""}
            />
              <FileCardList
                key={selectedSubDocType && selectedSubDocType.sub_doc_type && selectedSubDocType.sub_doc_type.id}
                onMove={(e: any) => onMove(e, selectedSubDocType && selectedSubDocType.sub_doc_type && selectedSubDocType.sub_doc_type.name)}
                onDrop={(e: any) => onDrop(e, selectedSubDocType && selectedSubDocType.sub_doc_type && selectedSubDocType.sub_doc_type.name)}
                groupName={selectedSubDocType && selectedSubDocType.sub_doc_type && selectedSubDocType.sub_doc_type.name}
                actualGroupName={docTypes.find(docType => docType.documents.some((doc: any) =>
                  doc.id === selectedSubDocType.id
                )
              ).name}
              documents={
                selectedSubDocType && [...selectedSubDocType.project_uploads
                .sort((a:any,b:any) => a.order-b.order)
                .map((ele: any) => {
                  return {
                    ...ele.upload,
                    ProjectUploadModel: ele
                  }
                })
              ]}
              isWaiting={isWaiting}
              canDownload={rolesContext.userHasPermission('all', 'Download')}
              {...projectPermissionContext.getDocumentProps(docType.name)}
              handleEdit={(doc: any) => handleEdit(doc)}
              handleRename={async(doc: any) => { 
                await showRename(doc, docType)
                await reloadSelectedSubDocTypeData()
              }}
              handleTrackPermit={(doc: any) => {
                setShowTrackPermitModal(true)
                setDocId(doc.id)
              }}
              handleReplace={async (newDoc: any, doc: any) => {
                // The upload has to succeed before anything downstream runs:
                // `deleteByFileId` drops the old document's permit rows, so a
                // failure part-way used to leave the document replaced by
                // nothing. uploadApi.create rejects now, and FileRow awaits
                // this handler without a catch of its own.
                try {
                  let response = await uploadApi.create(newDoc);

                  await handleReplace(
                    response,
                    selectedSubDocType && selectedSubDocType.project_doc_type_id,
                  );

                  let payload = {
                    newDoc: {
                      ...response,
                      project_doc_type_id:
                        selectedSubDocType &&
                        selectedSubDocType.project_doc_type_id,
                      project_id: projectId,
                    },
                    oldDoc: doc,
                  };

                  await permitApi.deleteByFileId(doc.ProjectUploadModel.id);
                  await uploadApi.project.replaceDocData(projectId, payload);
                  await reloadSelectedSubDocTypeData();
                  projectContext.loadDocs(
                    projectId,
                    searchRef.current?.value || '',
                  );
                } catch (err) {
                  showApiError(err);
                }
              }}
              handleDownload={(doc: any) => handleDownload(doc)}
              onUpload={async(newFile: any) => {
                await handleUpload(
                  newFile,
                  selectedSubDocType.project_doc_type_id,
                )
                await reloadSelectedSubDocTypeData()
              }}
              minimumImageDimensions={
                {
                    width: 20,
                    height: 20,
                  }
              }
              handleDelete={async(doc: any) => {
                await handleDelete(doc)
              }}
              permCheck={rolesContext.userHasPermission}
              label="Project Documents"
              allowedTypes={allowedTypes}
              document_date={projectContext.document_date}
              isAdmin={isAdmin}
              isProjectDocumentsPage={true}
              docTypes={docTypes}
              setShouldReloadDocumentsPage={setShouldReloadDocumentsPage}
              documentType={selectedSubDocType}
              projectId={projectId}
              selectedSubDocType={selectedSubDocType}
              setSelectedSubDocType={setSelectedSubDocType}
              reloadSelectedSubDocTypeData={reloadSelectedSubDocTypeData}
              showAddSubFolderIcon={true}
              canManageSubfolder={rolesContext.userHasPermission('all', 'Manage Subfolders')}
            />
          </>
        )}
        {!selectedRegSubFolder && !selectedSubDocType && (docTypes.filter(e => e.name !== 'Fixed Details Project')
          .map((documentType: any) => {
            if (
              documentType.name === 'Regulations' &&
              shouldRenderRegulations()
            ) {
              const documentGroupId: number =
                (projectContext.project?.document_group ?? 0) ||
                (documentType.documents || []).reduce((acc: number, d: any) => {
                  if (acc) return acc;
                  return (
                    d.document_group_id ||
                    d.ProjectRegulationUploadModel?.document_group_id ||
                    0
                  );
                }, 0);

              return (
                <FileCardList
                  key={documentType.id || documentType.name}
                  groupName={documentType.name}
                  documents={documentType.documents || []}
                  isWaiting={projectRegUploading}
                  canDownload={rolesContext.userHasPermission('all', 'Download')}
                  canEdit={canEditDocuments}
                  canRename={false}
                  canReplace={false}
                  canDelete={rolesContext.userHasPermission('delete', 'Project Documents')}
                  canUpload={rolesContext.userHasPermission('update', 'Project Documents')}
                  handleEdit={(doc: any) => handleEdit(doc)}
                  handleRename={() => {}}
                  handleReplace={() => {}}
                  handleDownload={(doc: any) => handleDownload(doc)}
                  handleDelete={async (doc: any) => {
                    if (!doc.ProjectRegulationUploadModel) return;
                    const uploadId = doc.ProjectRegulationUploadModel?.upload_id || doc.id;
                    await handleProjectRegDelete(uploadId, projectId);
                  }}
                  onUpload={async (newFile: any) => {
                    await handleProjectRegUpload(newFile, documentGroupId, null);
                  }}
                  permCheck={rolesContext.userHasPermission}
                  label="Project Documents"
                  allowedTypes={allowedTypes}
                  document_date={projectContext.document_date}
                  isAdmin={isAdmin}
                  isProjectDocumentsPage={true}
                  docTypes={docTypes}
                  setShouldReloadDocumentsPage={setShouldReloadDocumentsPage}
                  documentType={documentType}
                  projectId={projectId}
                  setSelectedSubDocType={(folder: any) => {
                    if (folder && folder.is_regulation_folder) {
                      setSelectedRegSubFolder(folder);
                    }
                  }}
                  showAddSubFolderIcon={rolesContext.userHasPermission('all', 'Manage Subfolders')}
                  canManageSubfolder={rolesContext.userHasPermission('all', 'Manage Subfolders')}
                  onMove={(e: any) => onMove(e, documentType.name)}
                  onDrop={(e: any) => onDrop(e, documentType.name)}
                  canDrag={rolesContext.userHasPermission('update', 'Project Documents')}
                  isRegulationsSection={true}
                  regulationDocumentGroupId={documentGroupId}
                  reloadDocs={() => projectContext.loadDocs(projectId, searchRef.current?.value || "")}
                />
              );
            }

            return (
              <FileCardList
                key={documentType.id}
                onMove={(e: any) => onMove(e, documentType.name)}
                onDrop={(e: any) => onDrop(e, documentType.name)}
                groupName={documentType.name}
                documents={documentType.documents}
                isWaiting={isWaiting}
                canDownload={rolesContext.userHasPermission('all', 'Download')}
                {...projectPermissionContext.getDocumentProps(docType.name)}
                canEdit={hasDocEditing}
                handleEdit={(doc: any) => handleEdit(doc)}
                handleRename={async (doc: any) => {
                  await showRename(doc, docType)
                  await reloadSelectedSubDocTypeData()
                }}
                handleTrackPermit={(doc: any) => {
                  setShowTrackPermitModal(true)
                  setDocId(doc.id)
                }}
                handleReplace={async (newDoc: any, doc: any) => {
                  // See the note on the other copy of this handler.
                  try {
                    let response = await uploadApi.create(newDoc);

                    await handleReplace(
                      response,
                      documentType.project_doc_type_id,
                    );

                    let payload = {
                      newDoc: {
                        ...response,
                        project_doc_type_id: documentType.project_doc_type_id,
                        project_id: projectId,
                      },
                      oldDoc: doc,
                    };

                    await permitApi.deleteByFileId(doc.ProjectUploadModel.id)
                    await uploadApi.project.replaceDocData(projectId, payload)
                    projectContext.loadDocs(projectId, searchRef.current?.value || "")
                  } catch (err) {
                    showApiError(err);
                  }
                }
                }
                handleDownload={(doc: any) => handleDownload(doc)}
                onUpload={async (newFile: any) => {
                  // Top-level folder: no sub-folder is selected here, so the
                  // doc type comes from the folder itself.
                  await handleUpload(newFile, documentType.project_doc_type_id)
                }}
                minimumImageDimensions={
                  {
                    width: 20,
                    height: 20,
                  }
                }
                handleDelete={async (doc: any) => {
                  await handleDelete(doc)
                }}
                permCheck={rolesContext.userHasPermission}
                label="Project Documents"
                allowedTypes={allowedTypes}
                document_date={projectContext.document_date}
                isAdmin={isAdmin}
                isProjectDocumentsPage={true}
                docTypes={docTypes}
                setShouldReloadDocumentsPage={setShouldReloadDocumentsPage}
                documentType={documentType}
                projectId={projectId}
                selectedSubDocType={selectedSubDocType}
                setSelectedSubDocType={setSelectedSubDocType}
                reloadSelectedSubDocTypeData={reloadSelectedSubDocTypeData}
                showAddSubFolderIcon={true}
                canManageSubfolder={rolesContext.userHasPermission('all', 'Manage Subfolders')}
              />
            );
          }))}
          {!docTypes.length && (
            <div className='no-documents'>
              <p>No documents found.</p>
            </div>
          )}
      </section>

      {/* load modals into DOM */}
      <ConfirmationModal
        show={confirm}
        handleClose={hideConfirm}
        handleConfirm={onConfirmOverwrite}
        title='Are you sure you want to delete this document?'
        subTitle='This can not be undone.'
        buttonText='Delete Document'
      />

      <WebViewerModal
        show={showEditor}
        onClose={hideEditor}
        onSave={() => onEdit()}
        guid={docGuid}
        id={docId}
        uploadId={docId}
        filename={filename}
        disableDownload={!rolesContext.userHasPermission("all", "Download")}
        canEdit={canEditDocuments}
      />

      <WebViewerModal
        show={showDownload}
        onClose={hideEditor}
        onSave={() => onEdit()}
        guid={docGuid}
        id={docId}
        uploadId={docId}
        canDownload={true}
        filename={filename}
        canEdit={canEditDocuments}
      />

      {/* invalid file type || DB error */}
      <SuccessModal
        show={showErrorModal}
        handleClose={() => setShowErrorModal(false)}
        handleSubmit={() => setShowErrorModal(false)}
        submitBtnText='OK'
        message={errorText}
        title='Error'
        isAlert
      />

      {showConvert && (
        <ConvertDocModal
          docs={convertQueue}
          handleClose={() => {
            setShowConvert(false);
            setConvertQueue([]);
          }}
          handleConverted={handleConverted}
          dequeue={dequeue}
        />
      )}
      <FileRenameModal
        show={rename}
        file={{ guid: docGuid, name: filename }}
        onCancel={hideRename}
        onSubmit={(fileName) => handleRename(fileName)}
      />

      <TrackPermitModal
        show={showTrackPermitModal}
        onCancel={() => setShowTrackPermitModal(false)}
        onSubmit={() => {
          setShowTrackPermitModal(false);
          selectedSubDocType ? reloadSelectedSubDocTypeData() : projectContext.loadDocs(projectId, searchRef.current?.value || "")
        }}
        fileId={docId}
      />
    </TooltipContextProvider>
  );
};

export default withProjectPermissionContext(ProjectDocuments);
